import React, { useEffect, useState } from 'react';

interface LiveIndicatorProps {
  /** Flash for ~1s when data just refreshed. */
  pulse?: boolean;
  /** Timestamp of last successful refetch (null = never). */
  lastSyncedAt: Date | null;
  className?: string;
}

function formatAgo(d: Date): string {
  const sec = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (sec < 5) return 'только что';
  if (sec < 60) return `${sec} сек назад`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} мин назад`;
  const hr = Math.floor(min / 60);
  return `${hr} ч назад`;
}

/**
 * Compact "live" badge: a green dot + a relative timestamp ("обновлено N сек назад").
 * The dot pulses briefly after a WS-triggered refetch so the operator can see
 * at a glance that the numbers on screen are fresh.
 */
const LiveIndicator: React.FC<LiveIndicatorProps> = ({ pulse, lastSyncedAt, className = '' }) => {
  // Re-render every 10s so the relative "N sec ago" stays current without a
  // global tick.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 10_000);
    return () => window.clearInterval(id);
  }, []);

  if (!lastSyncedAt) {
    return (
      <span className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground ${className}`}>
        <span className="inline-block w-2 h-2 rounded-full bg-muted-foreground/40" />
        офлайн
      </span>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs text-muted-foreground ${className}`}
      title={`Последняя синхронизация: ${lastSyncedAt.toLocaleTimeString('ru-RU')}`}
    >
      <span className="relative inline-flex">
        <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
        {pulse && (
          <span className="absolute inset-0 inline-block w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
        )}
      </span>
      Live · {formatAgo(lastSyncedAt)}
    </span>
  );
};

export default LiveIndicator;
